/* App state: server data + UI state, with SSE-driven refresh. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { InterventionProfile } from '@flourish/schema';
import { api, subscribe, type Capabilities, type EntityView, type JobView, type ScenarioView, type SignalView, type StrategyView } from './api.js';
import { clearLocalCache } from './local.js';

export type DrawerState =
  | { kind: 'cell'; strategyId: string; scenarioId: string }
  | { kind: 'iv-cell'; strategyId: string; iid: string; scenarioId: string }
  | { kind: 'scenario'; scenarioId: string }
  | { kind: 'strategy-editor'; strategyId: string | null }
  | { kind: 'no-regret'; strategyId: string }
  | { kind: 'most-likely'; entityId: string }
  | { kind: 'blindspots' }
  | { kind: 'entity-editor'; entityId: string | null }
  | { kind: 'signals' }
  | { kind: 'workspace' }
  | null;

export interface AppState {
  user: { id?: string; email: string; name: string; role?: string } | null;
  loading: boolean;
  capabilities: Capabilities | null;
  entities: EntityView[];
  scenarios: ScenarioView[];
  signals: SignalView[];
  strategiesByEntity: Record<string, StrategyView[]>;
  customIvsByEntity: Record<string, InterventionProfile[]>;
  activeEntityId: string;
  expanded: Record<string, boolean>;
  drawer: DrawerState;
  capabilityModal: string | null;          // entity type key
  jobs: Record<string, JobView>;
  theme: 'light' | 'dark';
  toast: string | null;
}

interface StoreApi {
  s: AppState;
  set: (patch: Partial<AppState>) => void;
  refresh: () => Promise<void>;
  refreshStrategies: (entityId: string) => Promise<void>;
  toast: (msg: string) => void;
  login: (email: string, password: string) => Promise<void>;
}

const Ctx = createContext<StoreApi | null>(null);

export function useStore(): StoreApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('store missing');
  return v;
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [s, setState] = useState<AppState>({
    user: null, loading: true, capabilities: null,
    entities: [], scenarios: [], signals: [], strategiesByEntity: {}, customIvsByEntity: {},
    activeEntityId: 'me', expanded: {}, drawer: null, capabilityModal: null,
    jobs: {}, theme: (localStorage.getItem('flourish_theme') as 'light' | 'dark') || 'light',
    toast: null,
  });
  const ref = useRef(s);
  ref.current = s;
  const set = useCallback((patch: Partial<AppState>) => setState(prev => ({ ...prev, ...patch })), []);

  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const toast = useCallback((msg: string) => {
    set({ toast: msg });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => set({ toast: null }), 2600);
  }, [set]);

  const refreshStrategies = useCallback(async (entityId: string) => {
    const [st, civ] = await Promise.all([api.strategies(entityId), api.customIvs(entityId)]);
    setState(prev => ({
      ...prev,
      strategiesByEntity: { ...prev.strategiesByEntity, [entityId]: st.strategies },
      customIvsByEntity: { ...prev.customIvsByEntity, [entityId]: civ.interventions },
    }));
  }, []);

  const refresh = useCallback(async () => {
    const [caps, ents, scns, sigs] = await Promise.all([api.capabilities(), api.entities(), api.scenarios(), api.signals()]);
    const strategiesByEntity: Record<string, StrategyView[]> = {};
    const customIvsByEntity: Record<string, InterventionProfile[]> = {};
    await Promise.all(ents.entities.map(async e => {
      const [st, civ] = await Promise.all([api.strategies(e.id), api.customIvs(e.id)]);
      strategiesByEntity[e.id] = st.strategies;
      customIvsByEntity[e.id] = civ.interventions;
    }));
    clearLocalCache();
    setState(prev => ({
      ...prev, capabilities: caps, entities: ents.entities, scenarios: scns.scenarios, signals: sigs.signals,
      strategiesByEntity, customIvsByEntity, loading: false,
      activeEntityId: ents.entities.some(e => e.id === prev.activeEntityId)
        ? prev.activeEntityId
        : (ents.entities.find(e => e.isSelf)?.id ?? ents.entities[0]?.id ?? 'me'),
    }));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    set({ user: res.user });
    await refresh();
  }, [refresh, set]);

  // boot: try existing session
  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        set({ user: me.user });
        await refresh();
      } catch {
        set({ loading: false });
      }
    })();
  }, [refresh, set]);

  // SSE
  useEffect(() => {
    if (!s.user) return;
    const off = subscribe({
      onJob: job => setState(prev => ({ ...prev, jobs: { ...prev.jobs, [job.id]: job } })),
      onChanged: (kind, data) => {
        if (kind === 'strategies.changed') {
          const entityId = (data as { entityId?: string }).entityId;
          if (entityId) void refreshStrategies(entityId);
        } else if (kind === 'scenarios.changed') {
          void api.scenarios().then(r => {
            clearLocalCache();
            setState(prev => ({ ...prev, scenarios: r.scenarios }));
          });
        } else if (kind === 'signals.changed') {
          void api.signals().then(r => setState(prev => ({ ...prev, signals: r.signals })));
        } else {
          void refresh();
        }
      },
    });
    return off;
  }, [s.user, refresh, refreshStrategies]);

  // theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', s.theme);
    localStorage.setItem('flourish_theme', s.theme);
  }, [s.theme]);

  const value = useMemo<StoreApi>(() => ({ s, set, refresh, refreshStrategies, toast, login }),
    [s, set, refresh, refreshStrategies, toast, login]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/* selectors */
export function activeEntity(s: AppState): EntityView | undefined {
  return s.entities.find(e => e.id === s.activeEntityId);
}
export function visibleScenarios(s: AppState): ScenarioView[] {
  return s.scenarios.filter(x => !x.hidden);
}
export function hiddenScenarios(s: AppState): ScenarioView[] {
  return s.scenarios.filter(x => x.hidden);
}
